import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useCart, type CartItem } from "@/context/cart-context";
import { formatPrice } from "@/lib/format";
import { Minus, Plus } from "lucide-react";

interface TicketSelectorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item: {
    id: string;
    name: string;
    price: number;
    type: "ticket" | "table" | "bottle";
    tierType?: string;
    description?: string;
    maxQuantity?: number;
  };
}

export function TicketSelector({ open, onOpenChange, item }: TicketSelectorProps) {
  const [quantity, setQuantity] = useState(1);
  const { addItem } = useCart();
  const navigate = useNavigate();

  const maxQty = item.maxQuantity || 10;

  const handleAdd = () => {
    const cartItem: CartItem = {
      type: item.type,
      itemId: item.id,
      name: item.name,
      quantity,
      unitPrice: item.price,
      tierType: item.tierType,
    };
    addItem(cartItem);
    setQuantity(1);
    onOpenChange(false);
    navigate("/checkout");
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="bg-cream border-ink/10">
        <SheetHeader>
          <SheetTitle className="font-heading text-lg uppercase tracking-wider text-ink">
            {item.name}
          </SheetTitle>
          <SheetDescription className="font-serif-elegant text-ink/60 italic">
            {item.description || `${formatPrice(item.price)} per ${item.type === "table" ? "table" : "ticket"}`}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-8 space-y-8">
          {/* Price */}
          <div className="text-center">
            <span className="font-heading text-4xl font-extrabold tracking-wider text-ink">
              {formatPrice(item.price)}
            </span>
            <span className="block font-serif-elegant text-ink/50 italic mt-1">
              per {item.type === "table" ? "table" : "ticket"}
            </span>
          </div>

          {/* Quantity selector */}
          <div className="flex items-center justify-center gap-6">
            <button
              onClick={() => setQuantity(Math.max(1, quantity - 1))}
              disabled={quantity <= 1}
              className="w-10 h-10 border border-ink/20 flex items-center justify-center text-ink hover:bg-ink hover:text-white transition-colors disabled:opacity-30"
            >
              <Minus className="w-4 h-4" />
            </button>
            <span className="font-heading text-2xl font-extrabold tracking-wider text-ink w-12 text-center">
              {quantity}
            </span>
            <button
              onClick={() => setQuantity(Math.min(maxQty, quantity + 1))}
              disabled={quantity >= maxQty}
              className="w-10 h-10 border border-ink/20 flex items-center justify-center text-ink hover:bg-ink hover:text-white transition-colors disabled:opacity-30"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>

          {/* Total */}
          <div className="border-t border-ink/10 pt-4">
            <div className="flex justify-between items-baseline">
              <span className="font-heading text-xs uppercase tracking-wider text-ink/60">Total</span>
              <span className="font-heading text-2xl font-extrabold tracking-wider text-ink">
                {formatPrice(item.price * quantity)}
              </span>
            </div>
          </div>

          {/* Add to cart */}
          <Button
            onClick={handleAdd}
            className="w-full bg-gold text-ink hover:bg-ink hover:text-white font-heading text-xs uppercase tracking-wider py-6"
          >
            Proceed to Checkout
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
